import { type ProjectDetailsMeta } from "@tokenterminal/tt-types"
import { SchemaScript } from "./SchemaScript"

interface DatasetSchemaOptions {
  name: string
  description: string
  baseUrl: string
  keywords?: string[]
  variableMeasured?: string[]
  temporalCoverage?: string
}

function makeGenericDatasetSchema(
  data: ProjectDetailsMeta,
  options: DatasetSchemaOptions
) {
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: options.name,
    description: options.description,
    url: `${options.baseUrl}/projects/${data.slug}`,
    creator: {
      "@type": "Organization",
      name: "Token Terminal",
      url: "https://tokenterminal.com",
    },
    distribution: [
      {
        "@type": "DataDownload",
        contentUrl: `https://api.tokentermianl.com/v2/projects/${data.slug}/metrics`,
        encodingFormat: "application/json",
      },
      {
        "@type": "DataDownload",
        contentUrl: `${options.baseUrl}/projects/${data.slug}`,
        encodingFormat: "text/csv",
      },
    ],
    license: "https://tokenterminal.com/terms",
    keywords: [
      data.name,
      data.symbol,
      ...data.market_sectors.map((sector) => sector.name),
      ...data.chains,
      "cryptocurrency",
      "blockchain",
      "financial metrics",
      "analytics",
      ...(options.keywords || []),
    ],
    temporalCoverage: options.temporalCoverage ?? "2020/..",
    variableMeasured: options.variableMeasured ?? [
      "Price",
      "Market Cap",
      "Trading Volume",
      "Revenue",
      "Total Value Locked",
    ],
  }
}

function makeDatasetSchema(
  data: ProjectDetailsMeta,
  metricName?: string,
  isEcosystemMetric?: boolean
) {
  const baseUrl = "https://tokenterminal.com/explorer"
  const entityName = isEcosystemMetric
    ? `the ${data.name} ecosystem`
    : data.name

  const options: DatasetSchemaOptions = {
    name:
      metricName && metricName !== "all" && metricName !== "projects"
        ? `${data.name} ${metricName} Metrics`
        : `${data.name} Financial and Analytics Data`,
    description:
      metricName && metricName !== "all" && metricName !== "projects"
        ? `Detailed ${metricName} metrics and analytics for ${entityName}, including historical data and trends.`
        : `Comprehensive financial and analytical metrics for ${entityName}, including key performance indicators, market data, and ecosystem analytics.`,
    baseUrl,
    keywords: metricName
      ? [metricName.toLowerCase(), `${metricName} metrics`]
      : undefined,
    variableMeasured: metricName ? [metricName] : undefined,
  }

  return makeGenericDatasetSchema(data, options)
}

function makeFinancialStatementSchema(data: ProjectDetailsMeta) {
  const options: DatasetSchemaOptions = {
    name: `${data.name} Financial Statement`,
    description: `Comprehensive financial statement for ${data.name}, including key performance indicators, market data, and ecosystem analytics.`,
    baseUrl: "https://tokenterminal.com/explorer",
    keywords: ["financial statement", "financial metrics", "financial data"],
    variableMeasured: ["Revenue", "Expenses", "Profit", "Cash Flow"],
    temporalCoverage: "2020/..",
  }

  return makeGenericDatasetSchema(data, options)
}

function makeOrganizationSchema(data: ProjectDetailsMeta) {
  const socialLinks = data.links.reduce((acc, link) => {
    if (
      link.title.toLowerCase() === "twitter" ||
      link.title.toLowerCase() === "discord" ||
      link.title.toLowerCase() === "github" ||
      link.title.toLowerCase() === "telegram"
    ) {
      acc.push(link.url)
    }
    return acc
  }, [] as string[])

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: data.name,
    url:
      data.links.find((link) => link.title.toLowerCase() === "website")?.url ||
      `https://tokenterminal.com/explorer/projects/${data.slug}`,
    logo: data.logo,
    sameAs: socialLinks,
    knowsAbout: [
      ...data.market_sectors.map((sector) => sector.name),
      ...data.chains.map((chain) => `${chain} blockchain`),
    ],
  }
}

interface Props {
  data: ProjectDetailsMeta
  metricName?: string
  isEcosystemMetric?: boolean
}

export const ProjectDatasetSchema = ({
  data,
  metricName,
  isEcosystemMetric,
}: Props) => (
  <SchemaScript
    id="dataset"
    schema={makeDatasetSchema(data, metricName, isEcosystemMetric)}
  />
)
export const ProjectOrganizationSchema = ({ data }: Props) => (
  <SchemaScript id="organization" schema={makeOrganizationSchema(data)} />
)
export const ProjectFinancialStatementSchema = ({ data }: Props) => (
  <SchemaScript
    id="financial-statement"
    schema={makeFinancialStatementSchema(data)}
  />
)
